import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@commenda/kakashi';
import type { PartnershipComputation } from './compute';
import { partnerRoleLabel } from './copy';
import { type OwnershipEntry, ownerDisplayName } from './types';

interface PartnershipSummaryProps {
  ids: string[];
  entries: Record<string, OwnershipEntry>;
  computation: PartnershipComputation;
  // ISO-4217 code shown in the capital column header.
  currency: string;
  gpApplies: boolean;
  country: string | undefined;
}

const fmtPercent = (n: number) => `${n.toFixed(2)}%`;
const fmtMoney = (n: number) =>
  n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const PartnershipSummary = ({
  ids,
  entries,
  computation,
  currency,
  gpApplies,
  country,
}: PartnershipSummaryProps) => {
  const { perOwnerCapital, totalCapitalAllocated, percentTotal } = computation;
  const emptyColSpan = gpApplies ? 4 : 3;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-text-primary">
          Ownership Summary
        </h3>
        <p className="text-sm text-text-secondary">
          Contributions are derived from each partner&apos;s ownership
          percentage of the total initial capital.
        </p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Partner</TableHead>
            {gpApplies ? <TableHead>Role</TableHead> : null}
            <TableHead className="text-right">% owned</TableHead>
            <TableHead className="text-right">
              Capital contribution ({currency})
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ids.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={emptyColSpan}
                className="py-6 text-center text-sm text-text-tertiary"
              >
                No partners added yet.
              </TableCell>
            </TableRow>
          ) : (
            ids.map((id, i) => {
              const entry = entries[id];
              if (!entry) return null;
              const isGp = !!entry.isGeneralPartner;
              return (
                <TableRow key={id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {ownerDisplayName(entry.owner)}
                      </span>
                      <Badge variant="gray" size="sm">
                        {entry.owner.kind === 'individual'
                          ? 'Individual'
                          : 'Entity'}
                      </Badge>
                    </div>
                  </TableCell>
                  {gpApplies ? (
                    <TableCell>
                      <Badge variant={isGp ? 'blue' : 'gray'} size="sm">
                        {partnerRoleLabel(country, isGp)}
                      </Badge>
                    </TableCell>
                  ) : null}
                  <TableCell className="text-right">
                    {fmtPercent(entry.ownershipPercent ?? 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    {fmtMoney(perOwnerCapital[i] ?? 0)}
                  </TableCell>
                </TableRow>
              );
            })
          )}

          <TableRow className="txt-small-bold bg-bg-secondary">
            <TableCell>Total</TableCell>
            {gpApplies ? <TableCell /> : null}
            <TableCell className="text-right">
              {fmtPercent(percentTotal)}
            </TableCell>
            <TableCell className="text-right">
              {fmtMoney(totalCapitalAllocated)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
